import React, { useEffect, useState, useCallback } from "react";
import { collection, getDocs, query, where, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import ConfirmDialog from "./ConfirmDialog";
import Toast from "./Toast";

const STATUS_LABEL = {
  pending: "Nieuw",
  approved: "Goedgekeurd",
  rejected: "Afgekeurd",
};

const STATUS_COLOR = {
  pending: "#3A4A68",
  approved: "#1E7B34",
  rejected: "#B3261E",
};

// Door gebruikers aangedragen koffiezaken die nog niet zijn aangesloten
// (collectie `shopSuggestions`, aangemaakt vanuit de app via
// lib/screens/suggest_shop_screen.dart). Deze admin-view laat een
// (super)admin nieuwe suggesties bekijken en goed- of afkeuren.
export default function ShopSuggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending | approved | rejected | all
  const [toast, setToast] = useState(null);
  const [confirmState, setConfirmState] = useState(null); // { suggestion, nextStatus }

  const showToast = (message, type = "success") => setToast({ message, type });

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const ref = collection(db, "shopSuggestions");
      const q = filter === "all" ? ref : query(ref, where("status", "==", filter));
      const snap = await getDocs(q);
      const items = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return bTime - aTime;
        });
      setSuggestions(items);
    } catch (error) {
      console.error("Error loading shop suggestions:", error);
      showToast("Fout bij laden van suggesties: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const requestReview = (suggestion, nextStatus) => {
    setConfirmState({ suggestion, nextStatus });
  };

  const applyReview = async () => {
    if (!confirmState) return;
    const { suggestion, nextStatus } = confirmState;
    try {
      await updateDoc(doc(db, "shopSuggestions", suggestion.id), {
        status: nextStatus,
        reviewedBy: auth.currentUser?.uid || null,
        reviewedByName: auth.currentUser?.email || null,
        reviewedAt: serverTimestamp(),
      });
      showToast(
        nextStatus === "approved"
          ? `"${suggestion.shopName}" gemarkeerd als goedgekeurd.`
          : `"${suggestion.shopName}" gemarkeerd als afgekeurd.`,
        "success"
      );
      setConfirmState(null);
      loadSuggestions();
    } catch (error) {
      console.error("Error reviewing shop suggestion:", error);
      showToast("Fout bij bijwerken van suggestie: " + error.message, "error");
      setConfirmState(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return "Onbekend";
    const date = timestamp.toDate();
    return date.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
  };

  const filters = [
    { id: "pending", label: "Nieuw" },
    { id: "approved", label: "Goedgekeurd" },
    { id: "rejected", label: "Afgekeurd" },
    { id: "all", label: "Alles" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <h2 style={{ margin: 0, color: "#3A4A68" }}>Aangedragen zaken</h2>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                padding: "8px 16px",
                background: filter === f.id ? "#3A4A68" : "rgba(58, 74, 104, 0.08)",
                color: filter === f.id ? "white" : "#3A4A68",
                border: "none",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 2px 10px rgba(58, 74, 104, 0.08)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#5b6b8c" }}>Suggesties laden...</div>
        ) : suggestions.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#5b6b8c" }}>
            Geen suggesties in deze categorie.
          </div>
        ) : (
          suggestions.map((s) => (
            <div
              key={s.id}
              style={{
                padding: "18px 20px",
                borderBottom: "1px solid rgba(58, 74, 104, 0.08)",
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div style={{ flex: "1 1 280px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                  <strong style={{ fontSize: "16px", color: "#3A4A68" }}>{s.shopName}</strong>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      padding: "3px 9px",
                      borderRadius: "12px",
                      color: "white",
                      background: STATUS_COLOR[s.status] || "#5b6b8c",
                    }}
                  >
                    {STATUS_LABEL[s.status] || s.status}
                  </span>
                </div>
                <div style={{ fontSize: "13px", color: "#5b6b8c", marginBottom: "4px" }}>
                  📍 {s.address || "Geen adres opgegeven"}
                  {typeof s.latitude === "number" && typeof s.longitude === "number" && (
                    <>
                      {" · "}
                      <a
                        href={`https://maps.google.com/?q=${s.latitude},${s.longitude}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#3A4A68" }}
                      >
                        kaart
                      </a>
                    </>
                  )}
                </div>
                {s.websiteOrInstagram && (
                  <div style={{ fontSize: "13px", color: "#5b6b8c", marginBottom: "4px" }}>
                    🔗 {s.websiteOrInstagram}
                  </div>
                )}
                {s.reason && (
                  <div style={{ fontSize: "13px", color: "#5b6b8c", marginBottom: "4px", fontStyle: "italic" }}>
                    "{s.reason}"
                  </div>
                )}
                <div style={{ fontSize: "12px", color: "#98a2b8", marginTop: "6px" }}>
                  Aangedragen door {s.submittedByEmail || "onbekend"} op {formatDate(s.createdAt)}
                </div>
              </div>

              {s.status === "pending" && (
                <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                  <button
                    onClick={() => requestReview(s, "approved")}
                    style={{
                      padding: "8px 16px",
                      background: "#1E7B34",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    ✔️ Goedkeuren
                  </button>
                  <button
                    onClick={() => requestReview(s, "rejected")}
                    style={{
                      padding: "8px 16px",
                      background: "rgba(179, 38, 30, 0.1)",
                      color: "#B3261E",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    ✕ Afkeuren
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.nextStatus === "approved" ? "Suggestie goedkeuren?" : "Suggestie afkeuren?"}
        message={
          confirmState
            ? `"${confirmState.suggestion.shopName}" markeren als ${
                confirmState.nextStatus === "approved" ? "goedgekeurd" : "afgekeurd"
              }? Dit informeert de indiener niet automatisch — neem zelf contact op met de zaak als je verder gaat.`
            : ""
        }
        confirmLabel={confirmState?.nextStatus === "approved" ? "Goedkeuren" : "Afkeuren"}
        danger={confirmState?.nextStatus === "rejected"}
        onConfirm={applyReview}
        onCancel={() => setConfirmState(null)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
